import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  DatasetRow,
  findColumn,
  getDataRowFieldValue,
  getDataRowFieldValueKey,
  getLinkedInlineColumns,
} from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { ChatElement } from '../chat-element.models';
import { ChatConversation, ChatMember, ChatMessage } from '../chat.models';

import { ChatSchemaService } from './chat-schema.service';

export interface MessagesColumns {
  rowId: string;
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  sender: string;
  conversation: string;
  attachments: string;
  modifiedAt: string;
}

export interface ConversationsColumns {
  rowId: string;
  id: string;
  name: string;
  members: string;
  createdBy: string;
}

export interface ConversationsInfoColumns {
  rowId: string;
  conversation: string;
  membersEmail: string;
  membersName: string;
  membersImage: string;
  lastMessageId: string;
  memberLinkColumn: string;
}

export interface MembersColumns {
  rowId: string;
  email: string;
  name: string;
  image: string;
  status?: string;
  statusDescription?: string;
}

const UNREAD_MESSAGES_COLUMN_NAME = 'unreadMessages';

@Injectable()
export class ChatElementService {
  private readonly schemaService = inject(SchemaService);
  private readonly chatSchemaService = inject(ChatSchemaService);

  private readonly messagesColumnsInitialized$$ = new BehaviorSubject(false);
  private readonly conversationsColumnsInitialized$$ = new BehaviorSubject(false);
  private readonly conversationsInfoColumnsInitialized$$ = new BehaviorSubject(false);
  private readonly membersColumnsInitialized$$ = new BehaviorSubject(false);

  private messagesColumns: MessagesColumns | null = null;
  private conversationsColumns: ConversationsColumns | null = null;
  private conversationsInfoColumns: ConversationsInfoColumns | null = null;
  private membersColumns: MembersColumns | null = null;

  messagesColumnsInitialized$ = this.messagesColumnsInitialized$$.asObservable();
  conversationsColumnsInitialized$ = this.conversationsColumnsInitialized$$.asObservable();
  conversationsInfoColumnsInitialized$ = this.conversationsInfoColumnsInitialized$$.asObservable();
  membersColumnsInitialized$ = this.membersColumnsInitialized$$.asObservable();

  cacheColumns(element: ChatElement): Observable<unknown> {
    return combineLatest({
      messages: this.chatSchemaService.getDatasetSchema$(element, 'messages').pipe(filter(Boolean)),
      conversations: this.chatSchemaService
        .getDatasetSchema$(element, 'conversations')
        .pipe(filter(Boolean)),
      conversationsInfo: this.chatSchemaService
        .getDatasetSchema$(element, 'conversationsInfo')
        .pipe(filter(Boolean)),
      members: this.getChatMembersSchema$(element).pipe(filter(Boolean)),
    }).pipe(
      tap(({ messages, conversations, conversationsInfo, members }) => {
        this.cacheMessagesColumns(element, messages);
        this.cacheConversationsColumns(element, conversations);
        this.cacheConversationsInfoColumns(element, conversationsInfo);
        this.cacheMembersColumns(element, members);
      }),
    );
  }

  datasetRowToMessage(datasetRow: DatasetRow, members: ChatMember[]): ChatMessage {
    const columns = this.getMessagesColumns();
    const member = members.find(
      (member) => member.email === getDataRowFieldValue(datasetRow, columns.sender),
    );

    return {
      id: +getDataRowFieldValue(datasetRow, columns.id),
      content: getDataRowFieldValue(datasetRow, columns.content),
      createdAt: getDataRowFieldValue(datasetRow, columns.createdAt),
      updatedAt: getDataRowFieldValue(datasetRow, columns.updatedAt),
      modifiedAt: getDataRowFieldValue(datasetRow, columns.modifiedAt),
      conversationId: +getDataRowFieldValue(datasetRow, columns.conversation),
      attachments: getDataRowFieldValue(datasetRow, columns.attachments) ?? [],
      sender: {
        id: member?.id ?? '',
        email: member?.email ?? getDataRowFieldValue(datasetRow, columns.sender),
        name: member?.name ?? '',
        image: member?.image ?? '',
      },
    };
  }

  datasetRowToConversation(
    datasetRow: DatasetRow,
    members: ChatMember[],
    currentUserEmail?: string,
  ): ChatConversation {
    const columns = this.getConversationsColumns();
    const name = getDataRowFieldValue(datasetRow, columns.name);
    const conversationMembers = getDataRowFieldValue(datasetRow, columns.members);

    // find all emails of conversation members
    const conversationMembersList: string | string[] = getDataRowFieldValueKey(
      datasetRow,
      columns.members,
    );
    let conversationMembersEmails: string[];
    if (Array.isArray(conversationMembersList)) {
      conversationMembersEmails = conversationMembersList.map(
        (userId) => members.find((member) => member.id === userId)?.email || '',
      );
    } else {
      conversationMembersEmails = [conversationMembersList];
    }

    const currentMember = members.find((member) => member.email === currentUserEmail);

    const conversation: ChatConversation = {
      id: +getDataRowFieldValue(datasetRow, columns.id),
      name,
      members: conversationMembers,
      emails: conversationMembersEmails,
      createdBy: getDataRowFieldValue(datasetRow, columns.createdBy),
      displayName:
        name ||
        conversationMembers
          .filter(
            (member: string) =>
              member !== currentMember?.name &&
              member !== currentMember?.email &&
              member !== currentMember?.id,
          )
          .join(', '),
    };

    if (conversation.members[0]) {
      conversation.image = this.getMemberByConversation(conversation.members[0], members)?.image;
    }

    return conversation;
  }

  datasetRowToConversationsInfo(datasetRow: DatasetRow): {
    conversationId: number;
    unreadMessages: number;
    lastMessageId: number;
  } {
    const columns = this.getConversationsInfoColumns();

    return {
      conversationId: +getDataRowFieldValue(datasetRow, columns.conversation),
      unreadMessages: datasetRow[UNREAD_MESSAGES_COLUMN_NAME],
      lastMessageId: datasetRow[columns.lastMessageId],
    };
  }

  getMemberByConversation(
    conversationMember: string,
    members: ChatMember[],
  ): ChatMember | undefined {
    return members.find((member) =>
      [member.id, member.name, member.email].includes(conversationMember),
    );
  }

  getMessagesColumns(): MessagesColumns {
    if (!this.messagesColumns) {
      throw new Error('Messages columns not initialized');
    }

    return this.messagesColumns;
  }

  getConversationsColumns(): ConversationsColumns {
    if (!this.conversationsColumns) {
      throw new Error('Conversations columns not initialized');
    }

    return this.conversationsColumns;
  }

  getConversationsInfoColumns(): ConversationsInfoColumns {
    if (!this.conversationsInfoColumns) {
      throw new Error('ConversationsInfo columns not initialized');
    }

    return this.conversationsInfoColumns;
  }

  getMembersColumns(): MembersColumns {
    if (!this.membersColumns) {
      throw new Error('Members columns from conversations info not initialized');
    }

    return this.membersColumns;
  }

  getChatMembersSchema$(element: ChatElement): Observable<BlazeSchema | undefined> {
    const chatMembersColumns = [
      element.props?.content?.input?.conversationsInfo?.membersName,
      element.props?.content?.input?.conversationsInfo?.membersEmail,
      element.props?.content?.input?.conversationsInfo?.membersImage,
    ];

    return combineLatest([
      this.chatSchemaService.getDatasetSchema$(element, 'members'),
      this.chatSchemaService.getDatasetSchema$(element, 'conversationsInfo'),
    ]).pipe(
      filter(
        ([membersSchema, conversationsInfoSchema]) =>
          Boolean(membersSchema) || Boolean(conversationsInfoSchema),
      ),
      distinctUntilChanged(isDeepEqual),
      switchMap(([membersSchema, conversationsInfoSchema]) => {
        if (membersSchema) {
          return this.schemaService.get(membersSchema.id);
        }

        if (!conversationsInfoSchema) {
          return of(undefined);
        }

        const chatMembersDatasetId = conversationsInfoSchema.columns.find(
          (column) => chatMembersColumns.includes(column.id) && column.link?.tableId,
        )?.link?.tableId;

        if (!chatMembersDatasetId) {
          return of(undefined);
        }

        return this.schemaService.get(chatMembersDatasetId);
      }),
    );
  }

  mapToChatMembers(datasetRows: DatasetRow[]): ChatMember[] {
    const columns = this.getMembersColumns();
    return datasetRows.map((row) => ({
      id: getDataRowFieldValue(row, columns.rowId),
      name: getDataRowFieldValue(row, columns.name),
      email: getDataRowFieldValue(row, columns.email),
      image: getDataRowFieldValue(row, columns.image),
      status: columns.status ? getDataRowFieldValue(row, columns.status) : undefined,
      statusDescription: columns.statusDescription
        ? getDataRowFieldValue(row, columns.statusDescription)
        : undefined,
    }));
  }

  private cacheMessagesColumns(element: ChatElement, schema: BlazeSchema): void {
    if (!element.props.content?.input?.messages) {
      return;
    }

    const { messages } = element.props.content.input;

    const messagesColumns: Partial<MessagesColumns> = {
      id: findColumn(schema.columns, messages.id)?.name,
      content: findColumn(schema.columns, messages.messageBody)?.name,
      createdAt: findColumn(schema.columns, messages.createdTime)?.name,
      updatedAt: findColumn(schema.columns, messages.updatedTime)?.name,
      modifiedAt: findColumn(schema.columns, messages.modifiedTime)?.name,
      sender: findColumn(schema.columns, messages.sender)?.name,
      conversation: findColumn(schema.columns, messages.conversation)?.name,
      attachments: findColumn(schema.columns, messages.attachments)?.name,
      rowId: schema.columns.find((column) => column.isId)?.name,
    };

    const requiredColumnsValidation = Object.entries(messagesColumns)
      .filter(([key]) => key !== 'modifiedAt')
      .map(([, val]) => val)
      .every(Boolean);
    if (requiredColumnsValidation) {
      this.messagesColumns = messagesColumns as MessagesColumns;
      this.messagesColumnsInitialized$$.next(true);
    }
  }

  private cacheConversationsColumns(element: ChatElement, schema: BlazeSchema): void {
    if (!element.props.content?.input?.conversations) {
      return;
    }

    const { conversations } = element.props.content.input;

    const conversationsColumns: Partial<ConversationsColumns> = {
      id: findColumn(schema.columns, conversations.id)?.name,
      name: findColumn(schema.columns, conversations.name)?.name,
      members: findColumn(schema.columns, conversations.members)?.name,
      createdBy: findColumn(schema.columns, conversations.createdBy)?.name,
      rowId: schema.columns.find((column) => column.isId)?.name,
    };

    if (Object.values(conversationsColumns).every(Boolean)) {
      this.conversationsColumns = conversationsColumns as ConversationsColumns;
      this.conversationsColumnsInitialized$$.next(true);
    }
  }

  private cacheConversationsInfoColumns(element: ChatElement, schema: BlazeSchema): void {
    if (!element.props.content?.input?.conversationsInfo) {
      return;
    }

    const { conversationsInfo } = element.props.content.input;

    const conversationsInfoColumns: Partial<ConversationsInfoColumns> = {
      conversation: findColumn(schema.columns, conversationsInfo.conversation)?.name,
      membersEmail: findColumn(schema.columns, conversationsInfo.membersEmail)?.name,
      membersName: findColumn(schema.columns, conversationsInfo.membersName)?.name,
      membersImage: findColumn(schema.columns, conversationsInfo.membersImage)?.name,
      lastMessageId: findColumn(schema.columns, conversationsInfo.lastMessageId)?.name,
      rowId: schema.columns.find((column) => column.isId)?.name,
    };

    conversationsInfoColumns.memberLinkColumn = schema.columns.find(
      (column) =>
        [
          conversationsInfoColumns.membersEmail,
          conversationsInfoColumns.membersImage,
          conversationsInfoColumns.membersName,
        ].includes(column.name) && column.link?.tableId,
    )?.name;

    if (Object.values(conversationsInfoColumns).every(Boolean)) {
      this.conversationsInfoColumns = conversationsInfoColumns as ConversationsInfoColumns;
      this.conversationsInfoColumnsInitialized$$.next(true);
    }
  }

  private cacheMembersColumns(element: ChatElement, schema: BlazeSchema): void {
    const conversationsInfoSchema = this.chatSchemaService.getDatasetSchema(
      element,
      'conversationsInfo',
    );

    const conversationsInfoNameColumn = conversationsInfoSchema?.columns.find(
      (column) => column.id === element.props.content.input?.conversationsInfo.membersName,
    );
    const conversationsInfoEmailColumn = conversationsInfoSchema?.columns.find(
      (column) => column.id === element.props.content.input?.conversationsInfo.membersEmail,
    );
    const conversationsInfoImageColumn = conversationsInfoSchema?.columns.find(
      (column) => column.id === element.props.content.input?.conversationsInfo.membersImage,
    );
    const idColumn = schema.columns.find((column) => column.isId)?.name;

    const nameColumn = findColumn(schema.columns, {
      id: getLinkedInlineColumns(conversationsInfoNameColumn?.formula)?.inlineColumnId,
      name: conversationsInfoNameColumn?.link?.displayColumn,
    })?.name;
    const emailColumn = findColumn(schema.columns, {
      id: getLinkedInlineColumns(conversationsInfoEmailColumn?.formula)?.inlineColumnId,
      name: conversationsInfoEmailColumn?.link?.displayColumn,
    })?.name;
    const imageColumn = findColumn(schema.columns, {
      name: conversationsInfoImageColumn?.link?.displayColumn,
      id: getLinkedInlineColumns(conversationsInfoImageColumn?.formula)?.inlineColumnId,
    })?.name;

    const { status, statusDescription } = element.props.content.input?.members || {};

    const memberStatusColumn = findColumn(schema.columns, status)?.name;
    const memberStatusDescriptionColumn = findColumn(schema.columns, statusDescription)?.name;
    const membersColumns: Partial<MembersColumns> = {
      rowId: idColumn,
      name: nameColumn,
      email: emailColumn,
      image: imageColumn,
      status: memberStatusColumn,
      statusDescription: memberStatusDescriptionColumn,
    };

    const optionalKeys = new Set(['status', 'statusDescription']);
    if (
      Object.entries(membersColumns).every(
        ([key, value]) => optionalKeys.has(key) || Boolean(value),
      )
    ) {
      this.membersColumns = membersColumns as MembersColumns;
      this.membersColumnsInitialized$$.next(true);
    }
  }
}
